// Shared constraint / permutation feasibility math.
//
// Single source of truth for both the main thread (App.js — violation/shadow
// helpers) and the perms Web Worker (permsWorker.js — the live feasibility
// engine). Previously duplicated near-verbatim across both, which let the two
// drift silently; that drift is exactly the desync class this project keeps
// fighting. Pure functions only: no React, no DOM, no monkey-patched prototypes,
// no ES2024-only Set.prototype.difference (uses local helpers below).

const sum = (arr) => arr.reduce((a, e) => a + e, 0);
const setDifference = (a, b) => new Set([...a].filter((x) => !b.has(x)));

export const complementUnrestrictedLetters = (pos, unrestrictedLetters) => {
  return pos.map(choices => {
    const restrictedChoices = choices.filter(choice => !unrestrictedLetters.includes(choice));
    if (restrictedChoices.length < choices.length) {
      return [...choices, '.'];
    } else {
      return choices;
    }
  });
};

export const getUnrestrictedLetters = (pos, min_counts, capped) => {
  const restrictedLetters = new Set([...min_counts.keys(), ...capped]);
  const allLetters = new Set(pos.flat());
  return [...allLetters].filter(letter => !restrictedLetters.has(letter));
};

export const replaceUnrestrictedLetters = (pos, unrestrictedLetters) => {
  return pos.map(choices => {
    const restrictedChoices = choices.filter(choice => !unrestrictedLetters.includes(choice));
    if (restrictedChoices.length === 0 && choices.length > 0) {
      return ['.'];
    } else if (restrictedChoices.length > 0 && restrictedChoices.length < choices.length) {
      return [...restrictedChoices, '.'];
    } else {
      return restrictedChoices;
    }
  });
};

export const updatePosWithEnteredLetters = (word, pos) => {
  return pos.map((choices, idx) => {
    return word[idx] === ' ' ? [] : [word[idx]];
  });
};

export const adjustConstraints = (word, constraints) => {
  const updatedMinCounts = new Map(constraints.min_counts);
  const enteredCounts = new Map();
  word.forEach(char => {
    if (char !== ' ') {
      if (!enteredCounts.has(char)) enteredCounts.set(char, 0);
      enteredCounts.set(char, enteredCounts.get(char) + 1);
    }
  });
  enteredCounts.forEach((count, char) => {
    if (updatedMinCounts.has(char)) {
      const newCount = updatedMinCounts.get(char) - count;
      if (newCount <= 0) updatedMinCounts.delete(char);
      else updatedMinCounts.set(char, newCount);
    }
  });
  return { ...constraints, min_counts: updatedMinCounts };
};

export const substituteSpecialSymbol = (modifiedPos, unrestrictedLetters) => {
  return modifiedPos.map(choices => {
    if (choices.includes('.')) {
      return [...choices.filter(choice => choice !== '.'), ...unrestrictedLetters];
    } else {
      return choices;
    }
  });
};

export const countRemainingChoicesForLetter = (choices, startIdx, letter) => {
  let count = 0;
  for (let i = startIdx; i < choices.length; i++) {
    for (const choice of choices[i]) {
      if (choice === letter) count++;
    }
  }
  return count;
};

export const updatePosWithValidLetters = (choices, constraints, modifiedPos, blankPositions, letterCounts = new Map(), currentPermutation = [], idx = 0) => {
  if (idx === choices.length) {
    const constrainedLetters = new Set([...constraints.min_counts.keys(), ...constraints.capped]);
    for (const letter of constrainedLetters) {
      const currentCount = letterCounts.get(letter) || 0;
      const targetCount = constraints.min_counts.get(letter) || 0;
      if (currentCount < targetCount) return 0;
      if (constraints.capped.includes(letter) && currentCount !== targetCount) return 0;
    }
    currentPermutation.forEach((choice, i) => {
      const posIndex = blankPositions[i];
      if (!modifiedPos[posIndex].includes(choice)) modifiedPos[posIndex].push(choice);
    });
    return 1;
  }

  let count = 0;
  for (const choice of choices[idx]) {
    const newLetterCounts = new Map(letterCounts);
    if (!newLetterCounts.has(choice)) newLetterCounts.set(choice, 0);
    newLetterCounts.set(choice, newLetterCounts.get(choice) + 1);

    let valid = true;
    const constrainedLetters = new Set([...constraints.min_counts.keys(), ...constraints.capped]);
    constrainedLetters.forEach((letter) => {
      const currentCount = newLetterCounts.get(letter) || 0;
      const targetCount = constraints.min_counts.get(letter) || 0;
      const remainingChoices = countRemainingChoicesForLetter(choices, idx + 1, letter);
      if (constraints.capped.includes(letter) && currentCount > targetCount) valid = false;
      if (currentCount + remainingChoices < targetCount) valid = false;
    });

    if (valid) {
      currentPermutation.push(choice);
      count += updatePosWithValidLetters(choices, constraints, modifiedPos, blankPositions, newLetterCounts, currentPermutation, idx + 1);
      currentPermutation.pop();
    }
  }
  return count;
};

export const get_capped_violations = (word, constraints) => {
  const violations = [];
  const letterCounts = new Map();
  const unrestricted_letters = getUnrestrictedLetters(constraints.pos, constraints.min_counts, constraints.capped);
  word.forEach((char) => {
    if (char !== ' ') {
      if (!letterCounts.has(char)) letterCounts.set(char, 0);
      letterCounts.set(char, letterCounts.get(char) + 1);
      if (unrestricted_letters.includes(char) && char !== '.') {
        if (!letterCounts.has('.')) letterCounts.set('.', 0);
        letterCounts.set('.', letterCounts.get('.') + 1);
      }
    }
  });
  const max_unrestricted = word.length - sum(Array.from(constraints.min_counts.values()));
  if ((letterCounts.get('.') || 0) > max_unrestricted) {
    word.forEach((char, idx) => {
      if (unrestricted_letters.includes(char)) violations.push(idx);
    });
  }
  constraints.capped.forEach((letter) => {
    const currentCount = letterCounts.get(letter) || 0;
    const minCount = constraints.min_counts.get(letter) || 0;
    if (currentCount > minCount) {
      word.forEach((char, idx) => { if (char === letter) violations.push(idx); });
    }
  });
  return violations;
};

export const get_mc_violations = (word, constraints) => {
  const violations = [];
  const letterCounts = new Map();
  let blanks = 0;
  word.forEach((char) => {
    if (char === ' ') { blanks++; } else {
      if (!letterCounts.has(char)) letterCounts.set(char, 0);
      letterCounts.set(char, letterCounts.get(char) + 1);
    }
  });
  let excessCapped = 0;
  constraints.capped.forEach((letter) => {
    const currentCount = letterCounts.get(letter) || 0;
    const minCount = constraints.min_counts.get(letter) || 0;
    if (currentCount > minCount) excessCapped += (currentCount - minCount);
  });
  let totalRequiredSpaces = 0;
  constraints.min_counts.forEach((minCount, letter) => {
    const currentCount = letterCounts.get(letter) || 0;
    totalRequiredSpaces += Math.max(0, minCount - currentCount);
  });
  if (blanks + excessCapped < totalRequiredSpaces) {
    letterCounts.forEach((currentCount, letter) => {
      const minCount = constraints.min_counts.get(letter) || 0;
      if (currentCount > minCount) {
        word.forEach((char, idx) => {
          if (char === letter && constraints.pos[idx].length > 1 && !violations.includes(idx)) violations.push(idx);
        });
      }
    });
  }
  return violations;
};

export const generateSubsets = (array, size) => {
  const result = [];
  const generate = (subset, start) => {
    if (subset.length === size) { result.push([...subset]); return; }
    for (let i = start; i < array.length; i++) {
      subset.push(array[i]);
      generate(subset, i + 1);
      subset.pop();
    }
  };
  generate([], 0);
  return result;
};


const perms_cache = new Map();

export const updatePosBasedOnConstraints = (word, constraints, cappedViolations, mcViolations, original_word) => {
  if (cappedViolations.length > 0 || mcViolations.length > 0) {
    return { count: 0, modifiedPos: [] };
  }
  if (word.join() === original_word.join()) {
    return null;
  }
  const u = getUnrestrictedLetters(constraints.pos, constraints.min_counts, constraints.capped);
  const key = word.map(c => u.includes(c) ? '.' : c).join();
  if (perms_cache.has(key)) return perms_cache.get(key);

  const replacedPos = replaceUnrestrictedLetters(constraints.pos, u);
  const modifiedPos = updatePosWithEnteredLetters(word, replacedPos);
  const adjustedConstraints = adjustConstraints(word, constraints);
  const blankPositions = modifiedPos.map((choices, idx) => choices.length === 0 ? idx : -1).filter(idx => idx !== -1);
  const blankChoices = blankPositions.map(idx => replacedPos[idx]);
  const count = updatePosWithValidLetters(blankChoices, adjustedConstraints, modifiedPos, blankPositions);
  const finalPos = substituteSpecialSymbol(modifiedPos, u);

  perms_cache.set(key, { count, modifiedPos: finalPos });
  return perms_cache.get(key);
};

export const hasFeasiblePermutations = (word, constraints, original_word) => {
  // null result == untouched draft (word === original_word) == feasible.
  const r = updatePosBasedOnConstraints(word, constraints, [], [], original_word);
  return r ? r.count > 0 : true;
};

export const findAllSmallestSubsetsToFix = (word, constraints, original_word) => {
  const filledPositions = word.map((char, idx) =>
    (char !== ' ' && constraints.pos[idx].length > 1) ? idx : -1
  ).filter(idx => idx !== -1);

  let smallestSize = filledPositions.length + 1;
  let smallestSubsets = [];

  for (let size = 1; size <= filledPositions.length; size++) {
    const subsets = generateSubsets(filledPositions, size);
    let foundFeasibleSubset = false;
    for (const subset of subsets) {
      const testWord = [...word];
      subset.forEach(pos => { testWord[pos] = ' '; });
      if (hasFeasiblePermutations(testWord, constraints, original_word)) {
        if (size < smallestSize) { smallestSize = size; smallestSubsets = [subset]; }
        else if (size === smallestSize) smallestSubsets.push(subset);
        foundFeasibleSubset = true;
      }
    }
    if (foundFeasibleSubset) break;
  }
  return smallestSubsets;
};

export const highlightErrorPositions = (word, constraints, cursor, original_word) => {
  const errorSubsets = findAllSmallestSubsetsToFix(word, constraints, original_word);
  let errorPositions;
  let errorPositionsInAllSubsets = [];
  let errorPositionsInNotAllSubsets = [];
  if (errorSubsets.some(subset => subset.includes(cursor))) {
    errorPositions = new Set(errorSubsets.filter(subset => subset.includes(cursor)).flat());
    errorPositionsInAllSubsets = new Set(Array.from(errorPositions).filter(position => errorSubsets.filter(subset => subset.includes(cursor)).every(subset => subset.includes(position))));
    errorPositionsInNotAllSubsets = setDifference(errorPositions, errorPositionsInAllSubsets);
  } else {
    errorPositions = new Set(errorSubsets.flat());
    errorPositionsInAllSubsets = new Set(Array.from(errorPositions).filter(position => errorSubsets.filter(subset => subset.includes(cursor)).every(subset => subset.includes(position))));
    errorPositionsInNotAllSubsets = setDifference(errorPositions, errorPositionsInAllSubsets);
  }
  return [Array.from(errorPositions), Array.from(errorPositionsInAllSubsets), Array.from(errorPositionsInNotAllSubsets)];
};
